import React, { useState } from 'react'
import { alpha, Alert, Box, Button, ButtonBase, Chip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import StageSection from './StageSection'
import JunkAnswerList from './JunkAnswerList'
import DisabledReason from '../system/DisabledReason'
import type { ThemeAIJunkDecision, ThemeAIScreening, ThemeAIScreeningRecordRow } from '../../types/gotypes'
import { JUNK_CATEGORY_ORDER, JUNK_NAME, junkCategoryLabel, junkCountLabel } from '../../utils/themeAIJunk'

export interface JunkRecordsView {
  records: ThemeAIScreeningRecordRow[]
  total: number
  offset: number
  pageSize: number
  loading: boolean
  onPageChange: (offset: number) => void
}

export interface JunkReviewStageProps {
  screening: ThemeAIScreening
  records: JunkRecordsView
  /** The category filter, or undefined for every flagged answer. */
  category?: string
  onCategoryChange: (category: string | undefined) => void
  saving: boolean
  /** Why the decision and Keep are closed to this person (they cannot change the task), or undefined. */
  writeBlockedReason?: string
  /** Why checking again is closed (AI access as well), or undefined. */
  checkBlockedReason?: string
  onSetDecision: (decision: ThemeAIJunkDecision) => Promise<unknown>
  onSetKept: (recordId: string, kept: boolean) => void
  onCheckAgain: () => void
  onDiscard: () => Promise<unknown>
  /** Back to Run. */
  onBack: () => void
}

const CHOICES: { decision: Exclude<ThemeAIJunkDecision, ''>; title: string; detail: string }[] = [
  { decision: 'keep', title: 'Keep all', detail: 'Code every answer as usual, junk included. Nothing changes.' },
  { decision: 'drop', title: 'Drop them', detail: 'Leave the flagged answers out. They get no code and are not in the base.' },
  {
    decision: 'code',
    title: 'Code them as junk / no answer',
    detail: `Give them code 99, “${JUNK_NAME}”, without using the model. They stay in the base and show as a row in tables.`,
  },
]

// The Run step's "Review" view: every flagged answer with why, the three
// things to do with them, and a Keep switch for the odd one that is fine. The
// choice holds until the data is replaced, so Run again does not re-code
// what was dropped.
const JunkReviewStage: React.FC<JunkReviewStageProps> = ({
  screening,
  records,
  category,
  onCategoryChange,
  saving,
  writeBlockedReason,
  checkBlockedReason,
  onSetDecision,
  onSetKept,
  onCheckAgain,
  onDiscard,
  onBack,
}) => {
  const [choice, setChoice] = useState<ThemeAIJunkDecision>(screening.decision)

  const byCategory = screening.by_category ?? {}
  const categories = JUNK_CATEGORY_ORDER.filter((key) => (byCategory[key] ?? 0) > 0)
  const everything = Object.values(byCategory).reduce((sum, n) => sum + n, 0)

  const handleSave = async () => {
    if (!choice) return
    try {
      await onSetDecision(choice)
      onBack()
    } catch {
      // The hook reports it; stay here so the choice is not lost.
    }
  }

  const handleDiscard = async () => {
    try {
      await onDiscard()
      onBack()
    } catch {
      // The hook reports it.
    }
  }

  return (
    <StageSection
      title="Junk answers"
      description={`${junkCountLabel(screening.flagged_count)} found in ${screening.response_count.toLocaleString()} answers. Choose what coding should do with them.`}
      actions={<Button color="info" startIcon={<ArrowBackIcon />} onClick={onBack}>Run</Button>}
    >
      {screening.unchecked_count > 0 && (
        <Alert severity="warning">
          {screening.unchecked_count.toLocaleString()} answers could not be checked by the model. The rules still checked them.
        </Alert>
      )}

      <Box role="radiogroup" aria-label="What to do with junk answers" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {CHOICES.map((item) => (
          <DisabledReason key={item.decision} reason={writeBlockedReason}>
            <ChoiceCard
              selected={choice === item.decision}
              title={item.title}
              detail={item.detail}
              onSelect={() => setChoice(item.decision)}
            />
          </DisabledReason>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <DisabledReason reason={writeBlockedReason}>
          <Button variant="contained" color="info" disabled={!choice || saving} onClick={handleSave}>
            Save decision
          </Button>
        </DisabledReason>
        <DisabledReason reason={checkBlockedReason}>
          <Button color="info" onClick={onCheckAgain} disabled={saving}>Check again</Button>
        </DisabledReason>
        <DisabledReason reason={writeBlockedReason}>
          <Button color="info" onClick={handleDiscard} disabled={saving}>Discard check</Button>
        </DisabledReason>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} role="group" aria-label="Filter by why an answer was flagged">
        <Chip
          label={`All (${everything.toLocaleString()})`}
          color={category === undefined ? 'info' : 'default'}
          variant={category === undefined ? 'filled' : 'outlined'}
          onClick={() => onCategoryChange(undefined)}
        />
        {categories.map((key) => (
          <Chip
            key={key}
            label={`${junkCategoryLabel(key)} (${(byCategory[key] ?? 0).toLocaleString()})`}
            color={category === key ? 'info' : 'default'}
            variant={category === key ? 'filled' : 'outlined'}
            onClick={() => onCategoryChange(key)}
          />
        ))}
      </Box>

      <JunkAnswerList {...records} writeBlockedReason={writeBlockedReason} onSetKept={onSetKept} />
    </StageSection>
  )
}

// The same selectable card Run uses for its re-run scope.
const ChoiceCard: React.FC<{ selected: boolean; disabled?: boolean; title: string; detail: string; onSelect: () => void }> = ({
  selected,
  disabled,
  title,
  detail,
  onSelect,
}) => (
  <ButtonBase
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    sx={(theme) => ({
      display: 'block',
      textAlign: 'left',
      p: 2,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: selected ? 'info.main' : 'divider',
      backgroundColor: selected ? alpha(theme.palette.info.main, 0.06) : 'transparent',
      opacity: disabled ? 0.6 : 1,
      transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
      '&:hover': { borderColor: selected ? 'info.main' : 'text.secondary' },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 2 },
    })}
  >
    <Typography variant="body1" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{detail}</Typography>
  </ButtonBase>
)

export default JunkReviewStage
